import { addDays, isPast } from 'date-fns'

import { DisputeUpdateDtoStatusEnum } from '@b-stock/dispute-api-client'
import type { StatusValues } from '@b-stock/fe-core/bstock-next/components'
import {
  getShippingLogic,
  ShippingTypeEnum,
  StatusLabelsEnum,
  StatusValueDisputeEnum,
  StatusValueOrderEnum,
  StatusValuePaymentEnum,
  StatusValueShipmentEnum,
} from '@b-stock/fe-core/bstock-next/components'
import { getShipmentDisplayStatus } from '@b-stock/fe-core/bstock-next/helpers'
import type { OrchestratedOrder } from '@b-stock/fe-core/bstock-next/queries'
import { parseOptionalISODate } from '@b-stock/fe-core/bstock-react'
import { StoreFrontOptionsSellerCollectsDtoShippingArrangedByEnum as ShippingArrangedBy } from '@b-stock/listing-api-client'
import {
  PaymentStatusEnum,
  OrderResponseDtoStatusEnum,
  AutoPayStateStatusEnum,
  AutoPayFinancingStateOutcomeEnum,
  LineItemPaymentStatusEnum,
} from '@b-stock/order-api-client'
import type { LineItem } from '@b-stock/order-api-client'
import { ShipmentStatus, ShipmentType } from '@b-stock/shipment-api-client'

import { StatusColor } from './constants'
import { OrderDetailsStatus } from './enums'
import { getSellerCollectsBstockFeeCents } from './sellerCollectsBstockFee'

// Re-export shipping utilities from fe-core for use in home-portal components
// WARNING: Do not export *directly* from fe-core, as that breaks Next.js builds
// https://gitlab.bstock.io/b-stock/code/three-mp/fe/home-portal/-/jobs/1457175
export {
  getShippingLogic,
  ShippingTypeEnum,
  StatusLabelsEnum,
  StatusValueDisputeEnum,
  StatusValueOrderEnum,
  StatusValuePaymentEnum,
  StatusValueShipmentEnum,
}

export const getDisputeWindowDeadline = (orderDetails: OrchestratedOrder) => {
  if (orderDetails.listing.sellerCollects === true) {
    const createdAt = parseOptionalISODate(orderDetails.createdAt)
    const disputeWindowDays = orderDetails.disputeWindowDays

    if (!createdAt || disputeWindowDays === undefined) {
      return undefined
    }

    return addDays(createdAt, disputeWindowDays)
  }

  return (
    parseOptionalISODate(orderDetails.disputeWindow?.disputeWindowEndDate) ??
    undefined
  )
}

/**
 * A storefront B-Stock does not dispute for. Offering the dispute flow there
 * sends the buyer somewhere B-Stock cannot act on their behalf, so the action
 * is withheld rather than left to fail at the end — see FP-3157.
 *
 * Only an explicit `false` counts; an unset flag is not a refusal.
 */
const refusesDisputes = (orderDetails: OrchestratedOrder) =>
  orderDetails.listing.sellerCollects === true &&
  orderDetails.sellerCollectsConfig?.disputesFacilitated === false

export const getIsReportable = (
  orderDetails: OrchestratedOrder,
  sellerCollectsEnabled = false
) => {
  const { orderDetailsStatus } = getStatus(
    orderDetails,
    false,
    false,
    false,
    sellerCollectsEnabled
  )
  const disputeWindowDeadline = getDisputeWindowDeadline(orderDetails)

  const isCanceledStatus =
    orderDetailsStatus === OrderDetailsStatus.CANCELED ||
    orderDetailsStatus === OrderDetailsStatus.CANCELED_HAS_CANCELLATION_FEE ||
    orderDetailsStatus === OrderDetailsStatus.CANCELED_REFUNDED
  const isReportable =
    !isCanceledStatus && orderDetailsStatus !== OrderDetailsStatus.CLOSED

  const isWithinSellerCollectsDisputeWindow =
    orderDetails.listing.sellerCollects === true
      ? !disputeWindowDeadline || !isPast(disputeWindowDeadline)
      : true

  return (
    isReportable &&
    !orderDetails.disputes.length &&
    isWithinSellerCollectsDisputeWindow &&
    !(sellerCollectsEnabled && refusesDisputes(orderDetails))
  )
}

type OrderAutoPayScope =
  | { payForId: string; payForType: 'contract' }
  | { payForId: string; payForType: 'listing' }

// Scope used to persist AutoPay via `upsertAutoPayMethod`. This must stay in
// sync with the scope `useOrderAutoPayMethod` (@b-stock/fe-core) derives
// internally for the saved-method lookup: both prefer `listing.contractId`
// before falling back to `contract._id`. If they diverge, the displayed method
// and the persisted method would target different scopes. fe-core exports the
// hook but no scope builder, so this function is intentionally kept.
export const getOrderAutoPayScope = (
  order: OrchestratedOrder
): OrderAutoPayScope => {
  // contractId is the canonical id order-service uses for AutoPay scheduling;
  // contract._id can point at a related contract detail document with a different id.
  const contractAutoPayId = order.listing.contractId ?? order.contract?._id

  return contractAutoPayId
    ? { payForId: contractAutoPayId, payForType: 'contract' }
    : { payForId: order.listing._id, payForType: 'listing' }
}

export const isOrderForAutoPay = (order: OrchestratedOrder): boolean =>
  order.autoPayState?.scheduledFor !== undefined

export const isAutoPayEnabled = (
  order: OrchestratedOrder,
  autoPayEnforced: boolean
): boolean => isOrderForAutoPay(order) && autoPayEnforced

export const isAutoPayDone = (autoPayState?: {
  status?: AutoPayStateStatusEnum
}): boolean =>
  autoPayState?.status === AutoPayStateStatusEnum.Completed ||
  autoPayState?.status === AutoPayStateStatusEnum.Failed ||
  autoPayState?.status === AutoPayStateStatusEnum.Skipped ||
  autoPayState?.status === AutoPayStateStatusEnum.Cancelled

export const canBuyerChangeShipping = (
  orderDetailsStatus: OrderDetailsStatus
): boolean =>
  orderDetailsStatus === OrderDetailsStatus.UNPAID ||
  orderDetailsStatus === OrderDetailsStatus.PAYMENT_PENDING ||
  orderDetailsStatus === OrderDetailsStatus.PAYMENT_FAILED ||
  orderDetailsStatus === OrderDetailsStatus.PAYMENT_OVERDUE ||
  orderDetailsStatus === OrderDetailsStatus.HOLD

const calculateTotalProcessing = (paymentItems: LineItem[]): number =>
  paymentItems.reduce((total, item) => {
    if (
      item.paymentStatus === LineItemPaymentStatusEnum.Processing ||
      item.paymentStatus === LineItemPaymentStatusEnum.FundsAvailable
    ) {
      return total + item.amount
    }
    return total
  }, 0)

const calculateTotalSuccess = (paymentItems: LineItem[]): number =>
  paymentItems.reduce((total, item) => {
    if (
      item.paymentStatus === LineItemPaymentStatusEnum.Paid ||
      item.paymentStatus === LineItemPaymentStatusEnum.Succeeded
    ) {
      return total + item.amount
    }
    return total
  }, 0)

// FP-2936 — narrower sibling of hasPaymentCollectedOrInFlight: only legs that are
// genuinely still moving, never settled ones. Checkout gates Pay Now on this.
export const hasPaymentInFlight = (
  lineItems: LineItem[] | undefined
): boolean =>
  calculateTotalProcessing(
    (lineItems ?? []).filter((item) => item.category === 'PAYMENT')
  ) > 0

export const hasPaymentCollectedOrInFlight = (
  lineItems: LineItem[] | undefined
): boolean => {
  const paymentItems = (lineItems ?? []).filter(
    (item) => item.category === 'PAYMENT'
  )
  return (
    calculateTotalSuccess(paymentItems) > 0 ||
    calculateTotalProcessing(paymentItems) > 0
  )
}

export const isShippingChangeLocked = (
  autoPayEnabled: boolean,
  autoPayState: { status?: AutoPayStateStatusEnum } | undefined,
  paymentStatus: PaymentStatusEnum | undefined,
  orderDetailsStatus: OrderDetailsStatus,
  isChargePastSchedule: boolean,
  hasPaymentActivity: boolean
): boolean => {
  if (hasPaymentActivity) return true
  // Lock shipping for any order with a Processing payment, regardless of autopay state
  if (paymentStatus === PaymentStatusEnum.Processing) return true
  if (autoPayEnabled) {
    return (
      !isAutoPayDone(autoPayState) &&
      orderDetailsStatus !== OrderDetailsStatus.PAYMENT_FAILED &&
      (autoPayState?.status === AutoPayStateStatusEnum.Processing ||
        autoPayState?.status ===
          AutoPayStateStatusEnum.ManualPaymentInProgress ||
        isChargePastSchedule)
    )
  }
  return false
}

export const getOrderTotalPaymentDue = (orderData?: OrchestratedOrder[]) => {
  const currencies: { [currency: string]: number } = {}
  orderData?.forEach((order) => {
    currencies[order.currencyCode] =
      (currencies[order.currencyCode] || 0) + order.amountDue
  })

  return currencies
}

/**
 * Currency to use for a batch-level total (e.g. the All Orders page's
 * Order total). Only trustworthy when every order in the batch shares one
 * currency. A mixed batch returns null: an amount summed across currencies
 * has no correct symbol, so callers omit the total rather than label it with
 * one that is known to be wrong. An empty or unread batch has nothing to
 * contradict the default and keeps USD.
 */
export const getOrdersDisplayCurrency = (
  orderData?: OrchestratedOrder[]
): string | null => {
  const firstCurrency = orderData?.[0]?.currencyCode

  if (!orderData || !firstCurrency) {
    return 'USD'
  }

  return orderData.every((order) => order.currencyCode === firstCurrency)
    ? firstCurrency
    : null
}

export type Status = {
  label: StatusLabelsEnum
  value: StatusValues
  color: StatusColor
  orderDetailsStatus: OrderDetailsStatus
}

const defaultStatus: Status = {
  label: StatusLabelsEnum.Unknown,
  value: '',
  color: StatusColor.Info,
  orderDetailsStatus: OrderDetailsStatus.UNPAID,
}

/**
 * Map enterprise order status string to proper Status with label/value.
 *
 * Enterprise statuses combine payment + shipping into single field.
 * This derives the proper label (Payment/Shipment/Order) and value for display.
 */
const getEnterpriseStatus = (enterpriseStatusString: string): Status => {
  const status = enterpriseStatusString.toLowerCase()

  // Unpaid states → Payment Status
  if (status === 'unpaid') {
    return {
      label: StatusLabelsEnum.Payment,
      value: StatusValuePaymentEnum.Unpaid,
      color: StatusColor.Info,
      orderDetailsStatus: OrderDetailsStatus.UNPAID,
    }
  }

  if (status === 'pending' || status === 'processing') {
    return {
      label: StatusLabelsEnum.Payment,
      value: StatusValuePaymentEnum.Processing,
      color: StatusColor.Info,
      orderDetailsStatus: OrderDetailsStatus.PAYMENT_PENDING,
    }
  }

  // Paid state → Payment Status (order paid)
  if (status === 'paid') {
    return {
      label: StatusLabelsEnum.Payment,
      value: StatusValuePaymentEnum.OrderPaid,
      color: StatusColor.Info,
      orderDetailsStatus: OrderDetailsStatus.PAID,
    }
  }

  // Shipping states → Shipment Status
  if (status === 'shipped') {
    return {
      label: StatusLabelsEnum.Shipment,
      value: StatusValueShipmentEnum.Shipped,
      color: StatusColor.Info,
      orderDetailsStatus: OrderDetailsStatus.PAID,
    }
  }

  if (status === 'delivered') {
    return {
      label: StatusLabelsEnum.Shipment,
      value: StatusValueShipmentEnum.Delivered,
      color: StatusColor.Success,
      orderDetailsStatus: OrderDetailsStatus.PAID,
    }
  }

  // Complete state → Order Status (closed)
  if (status === 'complete') {
    return {
      label: StatusLabelsEnum.Order,
      value: StatusValueOrderEnum.Closed,
      color: StatusColor.Success,
      orderDetailsStatus: OrderDetailsStatus.CLOSED,
    }
  }

  // Canceled state → Order Status (canceled)
  if (status === 'canceled' || status === 'cancelled') {
    return {
      label: StatusLabelsEnum.Order,
      value: StatusValueOrderEnum.Canceled,
      color: StatusColor.Canceled,
      orderDetailsStatus: OrderDetailsStatus.CANCELED,
    }
  }

  return {
    label: StatusLabelsEnum.Payment,
    value: StatusValuePaymentEnum.Unpaid,
    color: StatusColor.Info,
    orderDetailsStatus: OrderDetailsStatus.UNPAID,
  }
}

/**
 * Shipment states B-Stock can still observe on a Seller Collects order, which
 * only happens where it books the freight itself.
 *
 * Every member is mapped on purpose, and the type is a full `Record` rather
 * than a `Partial` so it stays that way. While five of them were missing, a
 * Whirlpool order sitting anywhere before pickup fell through to the Contact
 * Seller branch below — which also stripped View Shipment, since
 * `getViewShipmentButtonAppearance` offers it only on a `Shipment` label. The
 * AC is that B-Stock-booked freight leads the card with its shipment status,
 * and that has to hold for the states before it moves as much as after.
 */
const SELLER_COLLECTS_SHIPMENT_VALUES: Record<
  ShipmentStatus,
  { value: StatusValueShipmentEnum; color: StatusColor }
> = {
  // Nothing is booked yet, so there is no carrier and no date to report.
  [ShipmentStatus.ReadyToBook]: {
    value: StatusValueShipmentEnum.AwaitingShipment,
    color: StatusColor.Info,
  },
  [ShipmentStatus.ReadyToSchedule]: {
    value: StatusValueShipmentEnum.AwaitingShipment,
    color: StatusColor.Info,
  },
  [ShipmentStatus.BookingInProgress]: {
    value: StatusValueShipmentEnum.AwaitingShipment,
    color: StatusColor.Info,
  },
  // Booked with a date against it: the same thing the buyer needs to know as
  // BOOKED, which is why both read as booked rather than one as scheduled.
  [ShipmentStatus.Scheduled]: {
    value: StatusValueShipmentEnum.Booked,
    color: StatusColor.Info,
  },
  // The carrier has it, so from the buyer's side it is on its way.
  [ShipmentStatus.PickedUp]: {
    value: StatusValueShipmentEnum.Shipped,
    color: StatusColor.Info,
  },
  [ShipmentStatus.Booked]: {
    value: StatusValueShipmentEnum.Booked,
    color: StatusColor.Info,
  },
  [ShipmentStatus.Shipped]: {
    value: StatusValueShipmentEnum.Shipped,
    color: StatusColor.Info,
  },
  [ShipmentStatus.OutForDelivery]: {
    value: StatusValueShipmentEnum.OutForDelivery,
    color: StatusColor.Info,
  },
  [ShipmentStatus.Delivered]: {
    value: StatusValueShipmentEnum.Delivered,
    color: StatusColor.Success,
  },
  [ShipmentStatus.DeliveryFailed]: {
    value: StatusValueShipmentEnum.DeliveryFailed,
    color: StatusColor.Error,
  },
  [ShipmentStatus.Cancelled]: {
    value: StatusValueShipmentEnum.Canceled,
    color: StatusColor.Error,
  },
}

/**
 * What a tracked shipment reads as when its status is not one we know.
 *
 * The Record above is exhaustive over `ShipmentStatus`, which is only the
 * states the generated client declares — the service can and does return
 * others. QA found STAGING's Whirlpool order sitting on `INITIAL`, which is in
 * no version of that enum, and an unmapped status dropped the card back to
 * Contact Seller and took View Shipment with it. B-Stock booked this freight,
 * so a shipment exists and the card leads with it either way; what it cannot
 * do is name a state it has never heard of.
 */
const UNRECOGNISED_TRACKED_SHIPMENT = {
  value: StatusValueShipmentEnum.AwaitingShipment,
  color: StatusColor.Info,
}

/** The card status for a tracked shipment, whatever the service calls it. */
const trackedShipmentValue = (status: string) =>
  (
    SELLER_COLLECTS_SHIPMENT_VALUES as Record<
      string,
      { value: StatusValueShipmentEnum; color: StatusColor } | undefined
    >
  )[status] ?? UNRECOGNISED_TRACKED_SHIPMENT

// fe-core's StatusValueOrderEnum has no member for either of these, and the
// status is home-portal's to name — the enterprise fallback casts a literal the
// same way. Exported because the dashboard pill keys off these values rather
// than the SELLER_COLLECTS bucket, which both states carry.
export const ORDER_CREATED_VALUE = 'orderCreated' as StatusValues
// Its own literal rather than StatusValueOrderEnum.Closed: that value is shared
// with ordinary orders, whose "closed" wording must not change.
export const ORDER_CLOSED_VALUE = 'orderClosed' as StatusValues
// B-Stock Financing approved the order: it is unpaid on purpose until its due
// date. fe-core's StatusValuePaymentEnum has no member for it, so the same
// literal cast as above.
export const FINANCED_VALUE = 'financed' as StatusValues

/** Whether B-Stock Financing approved this order (order's financing outcome). */
export const isFinancingApplied = (
  data: Pick<OrchestratedOrder, 'autoPayState'>
): boolean =>
  data.autoPayState?.financing?.outcome ===
  AutoPayFinancingStateOutcomeEnum.Applied

/**
 * Auto-pay states in which the repayment is no longer waiting for its due date:
 * it is being charged, it failed, or it is done. getStatus already says each of
 * these (processing, payment failed, paid), so "Financed" steps aside for them.
 */
const REPAYMENT_UNDERWAY_AUTO_PAY_STATES: ReadonlySet<
  AutoPayStateStatusEnum | undefined
> = new Set([
  AutoPayStateStatusEnum.Processing,
  AutoPayStateStatusEnum.ManualPaymentInProgress,
  AutoPayStateStatusEnum.Failed,
  AutoPayStateStatusEnum.Completed,
])

/**
 * "Financed" for an open order B-Stock Financing approved whose repayment is
 * still waiting for its due date, or null. The repayment is an auto-pay
 * scheduled for that date, so without this the order reads like a card auto-pay
 * ("will pay on") or, once the timer passes, "processing" — neither is true of a
 * financed order nobody is charging yet.
 *
 * It stays a payment the buyer owes, so the details status is the unpaid one
 * (overdue past the deadline): Pay Now and the payment deadline still show.
 * Once money moves — paid, a payment or auto-pay processing or failed — the
 * order reads exactly as any other order does in that state.
 */
const getFinancedStatus = (
  data: OrchestratedOrder,
  isPastDue: boolean,
  hasFailedPayment: boolean
): Status | null => {
  if (
    data.status !== OrderResponseDtoStatusEnum.Open ||
    data.payment?.status === PaymentStatusEnum.Paid ||
    data.payment?.status === PaymentStatusEnum.Processing ||
    hasFailedPayment ||
    REPAYMENT_UNDERWAY_AUTO_PAY_STATES.has(data.autoPayState?.status) ||
    !isFinancingApplied(data)
  ) {
    return null
  }

  return {
    label: StatusLabelsEnum.Payment,
    value: FINANCED_VALUE,
    color: StatusColor.Info,
    orderDetailsStatus: isPastDue
      ? OrderDetailsStatus.PAYMENT_OVERDUE
      : OrderDetailsStatus.UNPAID,
  }
}

/**
 * Whether B-Stock can see this Seller Collects order's shipment move.
 *
 * Only where B-Stock books the freight does the carrier report movement back.
 * Everywhere else the shipment record exists but says nothing B-Stock has
 * observed — an AT&T order read "Ready to Schedule" off one, hiding the fact
 * that its fee was paid and the order closed. Anything that shows shipment
 * detail on these orders has to ask this rather than restate the rule, or the
 * two drift and a buyer is told about a delivery B-Stock never tracked.
 */
const tracksSellerCollectsShipment = (
  data: Pick<OrchestratedOrder, 'sellerCollectsConfig'>
): boolean =>
  data.sellerCollectsConfig?.shippingArrangedBy ===
  ShippingArrangedBy.BookedThroughBstock

/**
 * Status for an open Seller Collects order. The buyer pays the seller directly
 * for the goods off-platform, so B-Stock cannot know whether that happened —
 * Unpaid, Partially Paid, Past Due and Payment Failed are all unknowable here.
 *
 * Reads "Order created" while open and "Order closed" once settled, on every
 * surface — the design's wording, which replaced the PRD's "Contact seller".
 */
const getSellerCollectsStatus = (data: OrchestratedOrder): Status => {
  const shipmentStatus = tracksSellerCollectsShipment(data)
    ? data.shipments[0]?.status
    : undefined
  const observedShipment = shipmentStatus
    ? trackedShipmentValue(shipmentStatus)
    : undefined

  if (observedShipment) {
    return {
      label: StatusLabelsEnum.Shipment,
      ...observedShipment,
      orderDetailsStatus: OrderDetailsStatus.SELLER_COLLECTS,
    }
  }

  // From the buyer's premium line item, not the order total: Standard Shipping
  // freight lands on that total, which made a fee-free GE order read as
  // fee-bearing.
  const isFeeBearing = getSellerCollectsBstockFeeCents(data.lineItems) > 0

  if (isFeeBearing && data.payment?.status === PaymentStatusEnum.Paid) {
    return {
      label: StatusLabelsEnum.Order,
      value: ORDER_CLOSED_VALUE,
      // Neutral, not Success: ORD-L2 draws this header grey like the open one.
      color: StatusColor.Info,
      orderDetailsStatus: OrderDetailsStatus.SELLER_COLLECTS,
    }
  }

  return {
    label: StatusLabelsEnum.Order,
    value: ORDER_CREATED_VALUE,
    color: StatusColor.Info,
    orderDetailsStatus: OrderDetailsStatus.SELLER_COLLECTS,
  }
}

export const getStatus = (
  data?: OrchestratedOrder, // TODO: add disputes to my orders query
  isChargePastSchedule = false,
  autoPayEnabled = false,
  enableEnterpriseOrders = false,
  sellerCollectsEnabled = false
): Status => {
  if (!data) {
    return defaultStatus
  }

  // Enterprise orders: derive status from original enterprise status string
  // Enterprise status combines payment + shipping, so we map to proper label/value
  const isEnterpriseOrder = !!data.isEnterpriseOrder
  if (isEnterpriseOrder && enableEnterpriseOrders) {
    const enterpriseStatus = data.enterpriseStatus
    if (enterpriseStatus) {
      return getEnterpriseStatus(enterpriseStatus)
    }
    // Fallback if enterpriseStatus field missing
    return {
      label: StatusLabelsEnum.Order,
      value: 'notAvailable' as StatusValues,
      color: StatusColor.Info,
      orderDetailsStatus: OrderDetailsStatus.UNPAID,
    }
  }

  const {
    shipments,
    shipping,
    status,
    payment,
    amountDue,
    buyerAccountId,
    paymentDeadline,
    totalAmount,
    type,
    paymentSummary,
    disputes,
  } = data

  const paymentDeadlineDate = parseOptionalISODate(paymentDeadline)
  const isPastDue = paymentDeadlineDate ? isPast(paymentDeadlineDate) : false
  const isPartiallyPaid =
    amountDue && amountDue !== 0 && amountDue < totalAmount
  const hasFailedPayment =
    payment?.status === PaymentStatusEnum.Failed ||
    payment?.status === PaymentStatusEnum.RequestFailed
  const hasFailedAutoPay =
    autoPayEnabled &&
    data.autoPayState?.status === AutoPayStateStatusEnum.Failed &&
    payment?.status !== PaymentStatusEnum.Paid

  const shipment = shipments.length ? shipments[0] : undefined
  // Ideally, `type` would be defined as a proper enum by the API client
  // (instead of just `string`) so we don't need this type assertion here
  const shippingType = shipping.type as ShipmentType
  const isBuyerArrangesOrPickup =
    shippingType === ShipmentType.BuyerArranged ||
    shippingType === ShipmentType.BuyerPickup

  if (status === OrderResponseDtoStatusEnum.Hold) {
    return {
      label: StatusLabelsEnum.Order,
      value: StatusValueOrderEnum.Hold,
      color: StatusColor.Warning,
      orderDetailsStatus: OrderDetailsStatus.HOLD,
    }
  }

  // Only Open is intercepted: Hold above and the Closed/canceled block below
  // are states B-Stock genuinely knows, so they keep today's behavior.
  if (
    sellerCollectsEnabled &&
    data.listing.sellerCollects === true &&
    status === OrderResponseDtoStatusEnum.Open
  ) {
    return getSellerCollectsStatus(data)
  }

  const financedStatus = getFinancedStatus(data, isPastDue, hasFailedPayment)
  if (financedStatus) {
    return financedStatus
  }

  // Auto-pay block: only entered for active states.
  // Failed exits via !hasFailedAutoPay above → PaymentFailed. Skipped and Cancelled fall through to amountDue logic below.
  if (
    autoPayEnabled &&
    status === OrderResponseDtoStatusEnum.Open &&
    data.autoPayState &&
    data.autoPayState.status !== AutoPayStateStatusEnum.Completed &&
    data.autoPayState.status !== AutoPayStateStatusEnum.Skipped &&
    data.autoPayState.status !== AutoPayStateStatusEnum.Cancelled &&
    payment?.status !== PaymentStatusEnum.Paid &&
    !hasFailedPayment &&
    !hasFailedAutoPay
  ) {
    if (
      data.autoPayState.status === AutoPayStateStatusEnum.Processing ||
      data.autoPayState.status ===
        AutoPayStateStatusEnum.ManualPaymentInProgress ||
      isChargePastSchedule
    ) {
      return {
        label: StatusLabelsEnum.Payment,
        value: StatusValuePaymentEnum.Processing,
        color: StatusColor.Success,
        orderDetailsStatus: OrderDetailsStatus.PAYMENT_PENDING,
      }
    }
    if (data.autoPayState.status === AutoPayStateStatusEnum.Ready) {
      return {
        label: StatusLabelsEnum.Payment,
        value: StatusValuePaymentEnum.Won,
        color: StatusColor.Success,
        orderDetailsStatus: OrderDetailsStatus.PAYMENT_PENDING,
      }
    }
  }

  if (status === OrderResponseDtoStatusEnum.Open) {
    const isAutoPayCompleted =
      autoPayEnabled &&
      data.autoPayState?.status === AutoPayStateStatusEnum.Completed

    if (hasFailedPayment || hasFailedAutoPay) {
      return {
        label: StatusLabelsEnum.Payment,
        value: StatusValuePaymentEnum.PaymentFailed,
        color: StatusColor.Error,
        orderDetailsStatus: OrderDetailsStatus.PAYMENT_FAILED,
      }
    }

    if (
      payment?.status === PaymentStatusEnum.Unpaid ||
      payment?.status === PaymentStatusEnum.Succeeded ||
      !payment?.status
    ) {
      if (isPastDue) {
        if (isPartiallyPaid) {
          return {
            label: StatusLabelsEnum.Payment,
            value: StatusValuePaymentEnum.PaymentFailed,
            color: StatusColor.Error,
            orderDetailsStatus: OrderDetailsStatus.PAYMENT_OVERDUE,
          }
        }

        return {
          label: StatusLabelsEnum.Payment,
          value: StatusValuePaymentEnum.Unpaid,
          color: StatusColor.Warning,
          orderDetailsStatus: OrderDetailsStatus.PAYMENT_OVERDUE,
        }
      }

      if (isPartiallyPaid) {
        return {
          label: StatusLabelsEnum.Payment,
          value: StatusValuePaymentEnum.PaymentFailed,
          color: StatusColor.Error,
          orderDetailsStatus: OrderDetailsStatus.PAYMENT_FAILED,
        }
      }

      return {
        label: StatusLabelsEnum.Payment,
        value: StatusValuePaymentEnum.Unpaid,
        color: StatusColor.Info,
        orderDetailsStatus: OrderDetailsStatus.UNPAID,
      }
    }

    if (payment.status === PaymentStatusEnum.Processing) {
      return {
        label: StatusLabelsEnum.Payment,
        value: StatusValuePaymentEnum.Processing,
        color: StatusColor.Info,
        orderDetailsStatus: OrderDetailsStatus.PAYMENT_PENDING,
      }
    }

    if (payment.status === PaymentStatusEnum.Paid || isAutoPayCompleted) {
      if (disputes.length > 0) {
        const isAnyDisputeOpen = disputes.find(
          (dispute) => dispute.status === DisputeUpdateDtoStatusEnum.Open
        )
        const isAnyDisputeInProgress = disputes.find(
          (dispute) => dispute.status === DisputeUpdateDtoStatusEnum.InProgress
        )
        const isAnyDisputeClosed = disputes.find(
          (dispute) => dispute.status === DisputeUpdateDtoStatusEnum.Closed
        )

        if (!isAnyDisputeClosed) {
          if (isAnyDisputeOpen) {
            return {
              label: StatusLabelsEnum.Dispute,
              value: StatusValueDisputeEnum.Open,
              color: StatusColor.Warning,
              orderDetailsStatus: OrderDetailsStatus.PAID,
            }
          }

          if (isAnyDisputeInProgress) {
            return {
              label: StatusLabelsEnum.Dispute,
              value: StatusValueDisputeEnum.InProgress,
              color: StatusColor.Warning,
              orderDetailsStatus: OrderDetailsStatus.PAID,
            }
          }
        }
      }

      if (isBuyerArrangesOrPickup) {
        if (
          shipment?.status === ShipmentStatus.Delivered ||
          shipment?.status === ShipmentStatus.PickedUp
        ) {
          return {
            label: StatusLabelsEnum.Shipment,
            value: StatusValueShipmentEnum.PickedUp,
            color: StatusColor.Success,
            orderDetailsStatus: OrderDetailsStatus.PAID,
          }
        }

        if (shipment?.status === ShipmentStatus.DeliveryFailed) {
          return {
            label: StatusLabelsEnum.Shipment,
            value: StatusValueShipmentEnum.PickupFailed,
            color: StatusColor.Error,
            orderDetailsStatus: OrderDetailsStatus.PAID,
          }
        }

        if (shipment?.status === ShipmentStatus.Scheduled) {
          return {
            label: StatusLabelsEnum.Shipment,
            value: StatusValueShipmentEnum.PickupScheduled,
            color: StatusColor.Info,
            orderDetailsStatus: OrderDetailsStatus.PAID,
          }
        }

        return {
          label: StatusLabelsEnum.Shipment,
          value: StatusValueShipmentEnum.ReadyToSchedule,
          color: StatusColor.Info,
          orderDetailsStatus: OrderDetailsStatus.PAID,
        }
      } else {
        if (shipment?.status === ShipmentStatus.Booked) {
          return {
            label: StatusLabelsEnum.Shipment,
            value: StatusValueShipmentEnum.Booked,
            color: StatusColor.Info,
            orderDetailsStatus: OrderDetailsStatus.PAID,
          }
        }

        if (shipment?.status === ShipmentStatus.Shipped) {
          return {
            label: StatusLabelsEnum.Shipment,
            value: getShipmentDisplayStatus(shipment.status, shippingType),
            color: StatusColor.Info,
            orderDetailsStatus: OrderDetailsStatus.PAID,
          }
        }

        if (shipment?.status === ShipmentStatus.OutForDelivery) {
          return {
            label: StatusLabelsEnum.Shipment,
            value: StatusValueShipmentEnum.OutForDelivery,
            color: StatusColor.Info,
            orderDetailsStatus: OrderDetailsStatus.PAID,
          }
        }

        if (shipment?.status === ShipmentStatus.Delivered) {
          // TODO Handle adjustment orders (Post-MVP)
          return {
            label: StatusLabelsEnum.Shipment,
            value: StatusValueShipmentEnum.Delivered,
            color: StatusColor.Success,
            orderDetailsStatus: OrderDetailsStatus.PAID,
          }
        }
        if (shipment?.status === ShipmentStatus.DeliveryFailed) {
          return {
            label: StatusLabelsEnum.Shipment,
            value: StatusValueShipmentEnum.DeliveryFailed,
            color: StatusColor.Error,
            orderDetailsStatus: OrderDetailsStatus.PAID,
          }
        }
        if (shipment?.status === ShipmentStatus.Cancelled) {
          return {
            label: StatusLabelsEnum.Shipment,
            value: StatusValueShipmentEnum.Canceled,
            color: StatusColor.Error,
            orderDetailsStatus: OrderDetailsStatus.PAID,
          }
        }

        // For FLAT_RATE and FREE_SHIPPING with no shipment or unhandled shipment status,
        // show "Awaiting Shipment" instead of "Order Paid"
        // This covers cases like: no shipment, ReadyToBook, BookingInProgress, Scheduled, etc.
        if (
          shippingType === ShipmentType.FlatRate ||
          shippingType === ShipmentType.Free
        ) {
          return {
            label: StatusLabelsEnum.Shipment,
            value: StatusValueShipmentEnum.AwaitingShipment,
            color: StatusColor.Info,
            orderDetailsStatus: OrderDetailsStatus.PAID,
          }
        }

        return {
          label: StatusLabelsEnum.Payment,
          value: StatusValuePaymentEnum.OrderPaid,
          color: StatusColor.Info,
          orderDetailsStatus: OrderDetailsStatus.PAID,
        }
      }
    }
  }

  if (
    status === OrderResponseDtoStatusEnum.Closed ||
    status === OrderResponseDtoStatusEnum.PendingClosed
  ) {
    const refundOrder = getRefundOrder(data)
    if (refundOrder) {
      return {
        label: StatusLabelsEnum.Order,
        value: StatusValueOrderEnum.Canceled,
        color: StatusColor.Canceled,
        orderDetailsStatus: OrderDetailsStatus.CANCELED_REFUNDED,
      }
    }

    const cancellationFeeOrder = getCancellationFeeOrder(data)
    if (cancellationFeeOrder) {
      if (cancellationFeeOrder.payment?.status === PaymentStatusEnum.Paid) {
        return {
          label: StatusLabelsEnum.Order,
          value: StatusValueOrderEnum.Canceled,
          color: StatusColor.Canceled,
          orderDetailsStatus: OrderDetailsStatus.CANCELED,
        }
      }

      return {
        label: StatusLabelsEnum.Order,
        value: StatusValueOrderEnum.CanceledUnpaid,
        color: StatusColor.Warning,
        orderDetailsStatus: OrderDetailsStatus.CANCELED_HAS_CANCELLATION_FEE,
      }
    }

    if (
      data.closure?.reason === 'PARTY_REQUESTED' ||
      data.closure?.reason === 'NON_PAYING_SUBJECT'
    ) {
      return {
        label: StatusLabelsEnum.Order,
        value: StatusValueOrderEnum.Canceled,
        color: StatusColor.Canceled,
        orderDetailsStatus: OrderDetailsStatus.CANCELED,
      }
    }

    return {
      label: StatusLabelsEnum.Order,
      value: StatusValueOrderEnum.Closed,
      color: StatusColor.Success,
      orderDetailsStatus: OrderDetailsStatus.CLOSED,
    }
  }

  console.warn(`Could not find status for order.`, 'Parameters:', {
    shipping,
    status,
    payment,
    amountDue,
    buyerAccountId,
    paymentDeadline,
    totalAmount,
    shipments,
    type,
    paymentSummary,
  })

  return defaultStatus
}

const getRefundOrder = (order: OrchestratedOrder) => {
  return order.relatedOrders.find(({ type }) => type === 'REFUND')
}

export const getCancellationFeeOrder = (order: OrchestratedOrder) => {
  return order.relatedOrders.find(({ type }) => type === 'CANCELLATION_FEE')
}

export const getBuyerNeedsToPay = (
  orderDetailsStatus: OrderDetailsStatus,
  orderData: OrchestratedOrder
) => {
  if (orderDetailsStatus === OrderDetailsStatus.PAYMENT_PENDING) {
    const amountDue = getBuyerAmountDueRemaining(orderData)
    return amountDue > 0
  }

  return (
    orderDetailsStatus === OrderDetailsStatus.UNPAID ||
    orderDetailsStatus === OrderDetailsStatus.PAYMENT_FAILED ||
    orderDetailsStatus === OrderDetailsStatus.PAYMENT_OVERDUE ||
    orderDetailsStatus === OrderDetailsStatus.CANCELED_HAS_CANCELLATION_FEE
  )
}

export interface AmountBreakdown {
  amountDue: number
  creditsUsed: number
}

export const getBuyerAmountDueBreakdown = (
  orderData: OrchestratedOrder,
  { credits } = { credits: 0 }
): AmountBreakdown => {
  const { isCanceled } = orderData
  const { payment, cancellationFee } = orderData.paymentSummary
  if (isCanceled) {
    const amountSum = cancellationFee.due
    const creditsUsed = calcCreditsUsed(amountSum, credits)

    return { amountDue: amountSum - creditsUsed, creditsUsed }
  }

  // TODO Handle adjustment orders (Post-MVP)
  const { totalProcessing, totalSuccess } = payment
  const orderTotalAmount = orderData.totalAmount

  const amountSum = orderTotalAmount - (totalProcessing + totalSuccess)
  const creditsUsed = calcCreditsUsed(amountSum, credits)

  return { amountDue: amountSum - creditsUsed, creditsUsed }
}

const calcCreditsUsed = (amountSum: number, credits: number) => {
  const hasEnoughCredits = credits >= amountSum
  const amountDue = hasEnoughCredits ? 0 : amountSum - credits
  return amountSum - amountDue
}

export const getBuyerAmountDueRemaining = (
  orderData: OrchestratedOrder,
  options = { credits: 0 }
) => {
  return getBuyerAmountDueBreakdown(orderData, options).amountDue
}
